import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	Package,
	User,
	MapPin,
	CreditCard,
	CheckCircle,
	XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Separator } from '@/common/ui/separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import useQueryGetPurchaseById from '@/modules/shop/api/hooks/useQueryGetPurchaseById'
import useMutationProcessPurchase from '@/modules/shop/api/hooks/useMutationProcessPurchase'
import { ShopHistoryStatus } from '@/schema'
import { PURCHASE_STATUS_OPTIONS } from '@/modules/shop/constants'
import {
	REWARD_TYPE_LABELS,
	CURRENCY_TYPE_LABELS,
} from '@/modules/shop/components/ShopItemsListing/constants'
import Spinner from '@/common/components/Spinner'

function PurchaseDetailPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
		null,
	)

	// Use the single purchase endpoint for better performance
	const {
		data: purchase,
		isLoading,
		refetch,
	} = useQueryGetPurchaseById({
		id: id!,
		enabled: !!id,
	})

	const { mutate: processPurchase, isPending: isProcessing } =
		useMutationProcessPurchase()

	const handleApprove = () => {
		setActionType('approve')
	}

	const handleReject = () => {
		setActionType('reject')
	}

	const confirmAction = () => {
		if (!purchase || !actionType) return

		processPurchase(
			{
				id: purchase.id,
				data: {
					status:
						actionType === 'approve'
							? ShopHistoryStatus.SUCCESS
							: ShopHistoryStatus.FAILED,
				},
			},
			{
				onSuccess: () => {
					toast.success(
						actionType === 'approve'
							? 'อนุมัติคำสั่งซื้อสำเร็จ'
							: 'ปฏิเสธคำสั่งซื้อสำเร็จ',
					)
					setActionType(null)
					refetch()
				},
			},
		)
	}

	const closeDialog = () => {
		setActionType(null)
	}

	if (isLoading) {
		return (
			<PageContainer>
				<div className="flex h-96 items-center justify-center">
					<Spinner />
				</div>
			</PageContainer>
		)
	}

	if (!purchase) {
		return (
			<PageContainer>
				<div className="flex h-96 flex-col items-center justify-center space-y-4">
					<h1 className="text-2xl font-bold">ไม่พบคำสั่งซื้อ</h1>
					<p className="text-muted-foreground">
						คำสั่งซื้อที่คุณต้องการดูไม่พบในระบบ
					</p>
					<Button onClick={() => navigate('/operator/shop/purchases')}>
						กลับไปหน้าคำสั่งซื้อ
					</Button>
				</div>
			</PageContainer>
		)
	}

	const statusOption = PURCHASE_STATUS_OPTIONS.find(
		(opt) => opt.value === purchase.status,
	)
	const statusLabel = statusOption?.label || purchase.status

	const statusVariant =
		purchase.status === 'PENDING'
			? 'secondary'
			: purchase.status === 'SUCCESS'
				? 'default'
				: purchase.status === 'FAILED'
					? 'destructive'
					: 'secondary'

	const rewardTypeLabel = purchase.item?.rewardType
		? REWARD_TYPE_LABELS[
				purchase.item.rewardType as keyof typeof REWARD_TYPE_LABELS
			] || purchase.item.rewardType
		: 'N/A'

	const currencyLabel = purchase.item?.currencyType
		? CURRENCY_TYPE_LABELS[
				purchase.item.currencyType as keyof typeof CURRENCY_TYPE_LABELS
			] || purchase.item.currencyType
		: 'เพชร'

	return (
		<PageContainer>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(-1)}
						className="h-8 w-8 p-0"
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div>
						<h1 className="text-2xl font-bold">รายละเอียดคำสั่งซื้อ</h1>
						<p className="text-muted-foreground">ID: {purchase.id}</p>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
					{/* Purchase Information */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Package className="h-5 w-5" />
								ข้อมูลคำสั่งซื้อ
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										สถานะ
									</label>
									<div className="mt-1">
										<Badge variant={statusVariant}>{statusLabel}</Badge>
									</div>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										จำนวน
									</label>
									<p className="text-sm font-medium">
										{purchase.amount.toLocaleString()} ชิ้น
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										วันที่สั่งซื้อ
									</label>
									<p className="text-sm">
										{new Date(purchase.createdAt).toLocaleString('th-TH', {
											year: 'numeric',
											month: 'long',
											day: 'numeric',
											hour: '2-digit',
											minute: '2-digit',
										})}
									</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										วันที่อัพเดทล่าสุด
									</label>
									<p className="text-sm">
										{new Date(purchase.updatedAt).toLocaleString('th-TH', {
											year: 'numeric',
											month: 'long',
											day: 'numeric',
											hour: '2-digit',
											minute: '2-digit',
										})}
									</p>
								</div>
							</div>

							{purchase.trackingNumber && (
								<>
									<Separator />
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											หมายเลขติดตาม
										</label>
										<p className="font-mono text-sm font-medium">
											{purchase.trackingNumber}
										</p>
									</div>
								</>
							)}

							{purchase.adminNotes && (
								<>
									<Separator />
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											หมายเหตุผู้ดูแลระบบ
										</label>
										<div className="rounded bg-muted p-3 text-sm">
											{purchase.adminNotes}
										</div>
									</div>
								</>
							)}
						</CardContent>
					</Card>

					{/* Player Information */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<User className="h-5 w-5" />
								ข้อมูลผู้เล่น
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div>
								<label className="text-sm font-medium text-muted-foreground">
									รหัสผู้เล่น
								</label>
								<p className="font-mono text-sm font-medium">
									{purchase.playerId}
								</p>
							</div>
							{/* Note: player object appears to be empty in schema, so we only show playerId */}
						</CardContent>
					</Card>

					{/* Item Information */}
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<CreditCard className="h-5 w-5" />
								ข้อมูลสินค้า
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{purchase.item?.imageUrl && (
								<div className="flex justify-center">
									<img
										src={purchase.item.imageUrl}
										alt={purchase.item.name}
										className="h-32 w-32 rounded-lg object-cover"
									/>
								</div>
							)}

							<div className="grid grid-cols-1 gap-4">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ชื่อสินค้า
									</label>
									<p className="text-lg font-semibold">
										{purchase.item?.name || 'N/A'}
									</p>
								</div>

								{purchase.item?.description && (
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											รายละเอียดสินค้า
										</label>
										<p className="text-sm text-muted-foreground">
											{purchase.item.description}
										</p>
									</div>
								)}

								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											ประเภทรางวัล
										</label>
										<div className="mt-1">
											<Badge variant="outline">{rewardTypeLabel}</Badge>
										</div>
									</div>
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											ราคาต่อหน่วย
										</label>
										<p className="text-sm font-medium">
											{purchase.item?.price?.toLocaleString() || 'N/A'}{' '}
											{currencyLabel}
										</p>
									</div>
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											จำนวนรางวัล
										</label>
										<p className="text-sm font-medium">
											{purchase.item?.amount?.toLocaleString() || 'N/A'}
										</p>
									</div>
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											ราคารวม
										</label>
										<p className="text-lg font-bold text-primary">
											{purchase.item?.price && purchase.amount
												? (
														purchase.item.price * purchase.amount
													).toLocaleString()
												: 'N/A'}{' '}
											{currencyLabel}
										</p>
									</div>
								</div>

								<Separator />

								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<label className="text-muted-foreground">
											วันที่สร้างสินค้า
										</label>
										<p>
											{purchase.item?.createdAt
												? new Date(purchase.item.createdAt).toLocaleDateString(
														'th-TH',
													)
												: 'N/A'}
										</p>
									</div>
									<div>
										<label className="text-muted-foreground">สถานะสินค้า</label>
										<div className="flex gap-2">
											<Badge
												variant={
													purchase.item?.isActive ? 'default' : 'secondary'
												}
											>
												{purchase.item?.isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
											</Badge>
										</div>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Shipping Address */}
					{purchase.shippingAddress && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<MapPin className="h-5 w-5" />
									ที่อยู่จัดส่ง
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ชื่อผู้รับ
									</label>
									<p className="font-medium">{purchase.shippingAddress.name}</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										เบอร์โทรศัพท์
									</label>
									<p className="font-mono">{purchase.shippingAddress.phone}</p>
								</div>
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										ที่อยู่
									</label>
									<div className="text-sm">
										<p>{purchase.shippingAddress.address}</p>
										<p className="text-muted-foreground">
											ตำบล{purchase.shippingAddress.subDistrict} อำเภอ
											{purchase.shippingAddress.district} จังหวัด
											{purchase.shippingAddress.province}{' '}
											{purchase.shippingAddress.postalCode}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)}
				</div>

				{/* Action Buttons */}
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={() => navigate('/operator/shop/purchases')}
					>
						กลับไปหน้าคำสั่งซื้อ
					</Button>
					{purchase?.status === 'PENDING' && (
						<>
							<Button
								onClick={handleApprove}
								disabled={isProcessing}
								className="bg-green-600 hover:bg-green-700"
							>
								<CheckCircle className="mr-2 h-4 w-4" />
								อนุมัติคำสั่งซื้อ
							</Button>
							<Button
								variant="destructive"
								onClick={handleReject}
								disabled={isProcessing}
							>
								<XCircle className="mr-2 h-4 w-4" />
								ปฏิเสธคำสั่งซื้อ
							</Button>
						</>
					)}
				</div>
			</div>

			{/* Confirmation Dialog */}
			{actionType && purchase && (
				<Dialog open={true} onOpenChange={closeDialog}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>
								{actionType === 'approve'
									? 'ยืนยันการอนุมัติ'
									: 'ยืนยันการปฏิเสธ'}
							</DialogTitle>
							<DialogDescription>
								คุณแน่ใจหรือไม่ที่จะ
								{actionType === 'approve' ? 'อนุมัติ' : 'ปฏิเสธ'}
								คำสั่งซื้อนี้?
							</DialogDescription>
						</DialogHeader>
						<div className="py-4">
							<div className="space-y-2 text-sm text-muted-foreground">
								<p>
									<strong>ผู้เล่น:</strong> {purchase.playerId}
								</p>
								<p>
									<strong>สินค้า:</strong> {purchase.item?.name || 'N/A'}
								</p>
								<p>
									<strong>ราคา:</strong>{' '}
									{purchase.item?.price && purchase.amount
										? (purchase.item.price * purchase.amount).toLocaleString()
										: 'N/A'}{' '}
									{purchase.item?.currencyType === 'DIAMOND'
										? 'เพชร'
										: 'เครดิต'}
								</p>
							</div>
						</div>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={closeDialog}
								disabled={isProcessing}
							>
								ยกเลิก
							</Button>
							<Button
								variant={actionType === 'approve' ? 'default' : 'destructive'}
								onClick={confirmAction}
								disabled={isProcessing}
							>
								{isProcessing
									? 'กำลังประมวลผล...'
									: actionType === 'approve'
										? 'อนุมัติ'
										: 'ปฏิเสธ'}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</PageContainer>
	)
}

export default withUserPermission(PurchaseDetailPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_SHOP],
})
